import React from "react";
import { BannerVariant, BannerProps } from "@/components/Banner";

// Mocked model context -------------------------------------------------------
export enum ModelStatus {
    UNAUTHORIZED = "unauthorized",
    NOT_DEPLOYED = "not_deployed",
    INITIALIZING = "initializing",
    NOT_RUNNING = "not_running",
    TRAINING = "training",
    READY = "ready",
    REVIEW = "review",
    DEPLOYING = "deploying",
    DEPLOYED = "deployed"
}

export type ModelStatusType = (typeof ModelStatus)[keyof typeof ModelStatus];

export const CopyConfig: Record<
    ModelStatusType,
    Omit<BannerProps, "className" | "colors" | "children">
> = {
    [ModelStatus.UNAUTHORIZED]: {
        variant: BannerVariant.WARNING,
        icon: "fa fa-circle",
        title: "Your Model Is Not Deployed Yet",
        description:
            "Get an API key to deploy your model and start the continuous learning process."
    },
    [ModelStatus.NOT_DEPLOYED]: {
        variant: BannerVariant.WARNING,
        icon: "fa fa-circle",
        title: "Your Model Is Not Deployed Yet",
        description:
            "Run the code below to deploy your model and start the continuous learning process."
    },
    [ModelStatus.INITIALIZING]: {
        variant: BannerVariant.INFO,
        icon: "far fa-spinner fa-spin",
        title: "Your Model Is Initializing",
        description: "We’re setting up your model. It will be ready shortly!"
    },
    [ModelStatus.NOT_RUNNING]: {
        variant: BannerVariant.WARNING,
        icon: "fa fa-circle",
        title: "Your Model Is Not Running Yet",
        description: "Select a deployment method from the left to start running your model."
    },
    [ModelStatus.TRAINING]: {
        variant: BannerVariant.INFO,
        icon: "far fa-spinner fa-spin",
        title: "We’re Training A New Model Version",
        description: "We’ll notify you when we have a new model for you to review."
    },
    [ModelStatus.READY]: {
        variant: BannerVariant.INFO,
        icon: "far fa-check",
        title: "You Have A New Model Version To Review",
        description: "We’ve trained a new and improved model version for you to review and use."
    },
    [ModelStatus.REVIEW]: {
        variant: BannerVariant.INFO,
        icon: "far fa-images",
        title: "You Have 10 Images To Review",
        description:
            "Start labelling them to help your model retrain, learn, and improve from these examples."
    },
    [ModelStatus.DEPLOYING]: {
        variant: BannerVariant.SUCCESS,
        icon: "far fa-spinner fa-spin",
        title: "Your Model Is Deploying",
        description:
            "Your model is currently deploying. We’ll notify you when there are new results to review."
    },
    [ModelStatus.DEPLOYED]: {
        variant: BannerVariant.SUCCESS,
        icon: "far fa-check",
        title: "Your Model Is Deployed",
        description: "Deploy on more data and we’ll surface outlier images for you to review."
    }
};
